/**
 * `/firm-knowledge` — what the firm has taught its agents and keeps for its
 * clients: the Library (the exemplar documents agents read, in folders) and
 * the Intake Templates (the questionnaires the firm sends). Members can look;
 * who changes things is the firm's choice in Settings > Permissions (everyone
 * by default). (The firm's standing instructions for its agents live in
 * Settings > Case Preparation; the workflows it runs live in Automations,
 * which a stale `?tab=skills` link redirects to.)
 */
import { Building2, Plus } from "lucide-react";
import { useState, type FC } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { automationsRedirectTarget } from "@/features/automations/routes";
import {
  AskCaseyTemplateDialog,
  type AskCaseyTarget
} from "../components/AskCaseyTemplateDialog";
import { IntakeTemplatesTab } from "../components/IntakeTemplatesTab";
import { LibraryTab } from "../components/LibraryTab";
import { NewTemplateMenu } from "../components/NewTemplateMenu";
import { useFirmLibrary } from "../hooks/useFirmLibrary";
import { useFirmScope } from "../hooks/useFirmScope";
import { useIntakeTemplates } from "../hooks/useIntakeTemplates";

import { useOnIntakeTemplateOpened } from "../intakeTemplateEvents";
import {
  firmKnowledgePath,
  firmKnowledgeTab,
  type FirmKnowledgeTab
} from "../routes";

const DESCRIPTION: Record<FirmKnowledgeTab, [string, string]> = {
  library: ["The documents every agent at ", " reads before it works."],
  intake_templates: ["The intake questionnaires ", " sends to clients."]
};

export const FirmKnowledgePage: FC = () => {
  const firm = useFirmScope();
  const library = useFirmLibrary(firm.scope);
  const templates = useIntakeTemplates(Boolean(firm.lawFirmId));
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = firmKnowledgeTab(`?${params.toString()}`);
  const [uploadOpen, setUploadOpen] = useState(false);
  const openTemplateId =
    tab === "intake_templates" ? params.get("template") : null;
  // The template editor takes the whole page; its own bar leads back.
  const building = Boolean(openTemplateId);
  const [askCasey, setAskCasey] = useState<AskCaseyTarget | null>(null);

  // Keep a superadmin's firm override; drop everything tab-specific.
  const go = (base: string) => {
    const override = params.get("law_firm_id");
    navigate(
      override
        ? `${base}${
            base.includes("?") ? "&" : "?"
          }law_firm_id=${encodeURIComponent(override)}`
        : base
    );
  };
  const switchTab = (next: FirmKnowledgeTab) =>
    go(firmKnowledgePath({ tab: next }));
  const openTemplate = (templateId: string | null) =>
    go(
      firmKnowledgePath(
        templateId ? { templateId } : { tab: "intake_templates" }
      )
    );

  // Casey drafted or revised a template from the side panel.
  useOnIntakeTemplateOpened((templateId) => {
    if (templateId !== openTemplateId) openTemplate(templateId);
  });

  // Skills became Automations: a stale `?tab=skills` link lands there.
  const moved = automationsRedirectTarget(location.search);
  if (moved) return <Navigate to={moved} replace />;

  if (!firm.lawFirmId) {
    return (
      <EmptyState
        icon={Building2}
        title="No Firm Yet"
        description="Firm Knowledge is where a firm shows its agents its best work. Join or set up a firm to use it."
        className="h-full"
        data-testid="firm-knowledge-empty"
      />
    );
  }

  const count = (n: number, testId: string) =>
    n ? (
      <span className="text-fg-4 tabular-nums" data-testid={testId}>
        {n}
      </span>
    ) : null;

  const segment = (
    <Tabs value={tab} onValueChange={(v) => switchTab(v as FirmKnowledgeTab)}>
      <TabsList data-testid="firm-knowledge-tabs">
        <TabsTrigger value="library" data-testid="tab-library">
          Library
        </TabsTrigger>
        <TabsTrigger
          value="intake_templates"
          data-testid="tab-intake-templates"
        >
          Intake Templates
          {count(templates.activeCount, "tab-intake-templates-count")}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );

  const [before, after] = DESCRIPTION[tab];
  const whose =
    firm.isSuperAdmin && firm.scope.lawFirmId ? "this firm" : "your firm";

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="firm-knowledge-page"
    >
      {building ? null : (
        <div className="flex shrink-0 px-6 pt-6 pb-3">
          <PageHeader
            title="Firm Knowledge"
            description={
              <>
                {before}
                {whose}
                {after}
              </>
            }
            actions={
              tab === "library" && firm.canEditLibrary ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setUploadOpen(true)}
                  data-testid="add-documents"
                >
                  <Plus />
                  Add Documents
                </Button>
              ) : tab !== "library" && !firm.permissionsReady ? (
                <Skeleton
                  className="h-7 w-24 rounded-md"
                  data-testid="new-template-loading"
                />
              ) : tab === "intake_templates" && firm.canEditLibrary ? (
                <NewTemplateMenu
                  templates={templates}
                  onCreated={openTemplate}
                  onAskCasey={() => setAskCasey({ kind: "draft" })}
                />
              ) : null
            }
            className="w-full"
          />
        </div>
      )}
      <div
        className="min-h-0 flex-1 overflow-hidden"
        data-building={building || undefined}
      >
        {tab === "intake_templates" ? (
          <IntakeTemplatesTab
            templates={templates}
            firm={firm}
            openTemplateId={openTemplateId}
            onOpenTemplate={openTemplate}
            onHome={() => switchTab("library")}
            onAskCasey={(template) =>
              setAskCasey({
                kind: "revise",
                templateId: template.template_id,
                name: template.name
              })
            }
            toolbarLeading={segment}
          />
        ) : (
          <LibraryTab
            library={library}
            firm={firm}
            uploadOpen={uploadOpen}
            onUploadOpenChange={setUploadOpen}
            toolbarLeading={segment}
          />
        )}
      </div>
      <AskCaseyTemplateDialog
        target={askCasey}
        onOpenChange={(open) => !open && setAskCasey(null)}
      />
    </div>
  );
};
